import React, { useEffect, useState } from "react";

type LocationOption = { name: string; type: "Entry" | "Exit"; id: number };

type DetectionAreaTabProps = {
  locationOptions: { name: string; type: "Entry" | "Exit" }[];
  selectedLocation: string;
  setSelectedLocation: React.Dispatch<React.SetStateAction<string>>;
  showAddLocation: boolean;
  setShowAddLocation: React.Dispatch<React.SetStateAction<boolean>>;
  cameraDevices: MediaDeviceInfo[];
  selectedCameraId: string;
  setSelectedCameraId: React.Dispatch<React.SetStateAction<string>>;
  videoRef: React.RefObject<HTMLVideoElement | null>
  areaPoints: { x: number; y: number }[];
  handlePointerDown: (idx: number) => () => void;
  timestamp: Date;
  handleSave: () => void;
};

const DetectionAreaTab: React.FC<DetectionAreaTabProps> = ({
  selectedLocation,
  setSelectedLocation,
  showAddLocation,
  setShowAddLocation,
  cameraDevices,
  selectedCameraId,
  setSelectedCameraId,
  videoRef,
  areaPoints,
  handlePointerDown,
  timestamp,
  handleSave,
}) => {
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [loadingLocations, setLoadingLocations] = useState<boolean>(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    setLoadingLocations(true);
    try {
      const response = await fetch("https://galaxy-alpr-be.onrender.com/api/locations");
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      setLocations(data);

      // Select first location by default if none selected
      if (data.length > 0 && !selectedLocation) {
        setSelectedLocation(data[0].name);
      }
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    } finally {
      setLoadingLocations(false);
    }
  };

  return (
    <>
      {/* Location and Camera Dropdowns */}
      <div className="mb-8 flex flex-col md:flex-row justify-center gap-4">
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Location
          </label>
          <div className="flex space-x-2">
            <select
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              disabled={loadingLocations}
            >
              {loadingLocations && <option value="">Loading locations...</option>}
              {!loadingLocations && locations.length === 0 && (
                <option value="">No locations available</option>
              )}
              {locations.map((loc) => (
                <option key={loc.id} value={loc.name}>
                  {loc.name} ({loc.type})
                </option>
              ))}
            </select>
            <select
              className="px-4 py-2 rounded border border-gray-300 bg-white shadow"
              value={selectedCameraId}
              onChange={(e) => setSelectedCameraId(e.target.value)}
            >
              {cameraDevices.length === 0 && (
                <option value="">No camera found</option>
              )}
              {cameraDevices.map((cam) => (
                <option key={cam.deviceId} value={cam.deviceId}>
                  {cam.label || `Camera ${cam.deviceId}`}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Camera Preview */}
      <div className="mb-8 flex justify-center">
        <div className="relative bg-gray-900 rounded-2xl overflow-hidden shadow-2xl w-[600px] h-[400px] flex items-center justify-center">
          <video
            ref={videoRef}
            className="w-full h-full object-cover rounded-2xl"
            autoPlay
            muted
            playsInline
          />
          {/* Overlay for detection area */}
          <svg
            id="detection-area-svg"
            className="absolute inset-0 w-full h-full pointer-events-auto"
            viewBox="0 0 600 400"
          >
            <polygon
              points={areaPoints.map((p) => `${p.x},${p.y}`).join(" ")}
              fill="rgba(34, 197, 94, 0.2)"
              stroke="#22c55e"
              strokeWidth="2"
              strokeDasharray="5,5"
            />
            {areaPoints.map((pt, idx) => (
              <circle
                key={idx}
                cx={pt.x}
                cy={pt.y}
                r={8}
                fill="#22c55e"
                stroke="#fff"
                strokeWidth="2"
                className="cursor-pointer"
                onPointerDown={handlePointerDown(idx)}
                style={{ touchAction: "none" }}
              />
            ))}
          </svg>
          {/* Timestamp */}
          <div className="absolute top-4 right-4 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm font-medium">
            {timestamp.toLocaleString(undefined, {
              hour12: false,
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })}
          </div>
          {/* Camera Label */}
          <div className="absolute top-4 left-4 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm font-medium">
            {cameraDevices.find((cam) => cam.deviceId === selectedCameraId)
              ?.label || "(Unknown Camera)"}
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-center mb-8">
        <button
          onClick={handleSave}
          className="bg-blue-500 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Save
        </button>
      </div>

      {/* Instructions */}
      <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-800 mb-2">
          Configuration Instructions:
        </h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Click and drag the corner points to adjust the detection area</li>
          <li>
            • The green highlighted area represents where license plates will be
            detected
          </li>
          <li>
            • Ensure the detection area covers the vehicle entry/exit points
          </li>
          <li>
            • Avoid including background areas that might cause false detections
          </li>
        </ul>
      </div>
    </>
  );
};

export default DetectionAreaTab;
